import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "./PlatformConfigurationPage.css";

const FEATURE_KEYS = [
  "spot_trading",
  "futures_trading",
  "p2p_trading",
  "staking",
  "launchpad",
  "swap",
  "deposit",
  "withdrawal",
];

const FEATURE_ITEMS = [
  { key: "spot_trading", label: "Spot Trading", icon: "fa-chart-line", desc: "Enable/disable spot buy & sell" },
  { key: "futures_trading", label: "Futures Trading", icon: "fa-layer-group", desc: "Enable/disable futures contracts" },
  { key: "p2p_trading", label: "P2P Trading", icon: "fa-exchange-alt", desc: "Enable/disable P2P buy/sell orders" },
  { key: "staking", label: "Staking", icon: "fa-coins", desc: "Enable/disable staking rewards" },
  { key: "launchpad", label: "Launchpad", icon: "fa-rocket", desc: "Enable/disable token launches" },
  { key: "swap", label: "Swap", icon: "fa-sync-alt", desc: "Enable/disable swap/trade feature" },
  { key: "deposit", label: "Deposit", icon: "fa-arrow-down", desc: "Enable/disable new deposits" },
  { key: "withdrawal", label: "Withdrawal", icon: "fa-arrow-up", desc: "Enable/disable withdrawals" },
];

const DEFAULT_FEATURE = { enabled: true, scheduled_at: null, scheduled_action: null };

const PlatformConfigurationPage = () => {
  const [features, setFeatures] = useState({});
  const [originalFeatures, setOriginalFeatures] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(null);
  const [toast, setToast] = useState(null);

  // Schedule form (per-feature scheduling)
  const [scheduleFeature, setScheduleFeature] = useState("");
  const [scheduleAction, setScheduleAction] = useState("disable");
  const [scheduleDatetime, setScheduleDatetime] = useState("");

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminService.getPlatformConfig();
      if (res?.success && res.data) {
        const data = res.data;
        const normalized = {};
        [...FEATURE_KEYS, "full_maintenance"].forEach((key) => {
          const v = data[key];
          normalized[key] = v && typeof v === "object"
            ? {
                enabled: !!v.enabled,
                scheduled_at: v.scheduled_at || null,
                scheduled_action: v.scheduled_action || null,
              }
            : { ...DEFAULT_FEATURE, enabled: !!v };
        });
        setFeatures(normalized);
        setOriginalFeatures(normalized);
      } else {
        // Fallback defaults
        const def = FEATURE_KEYS.concat("full_maintenance").reduce(
          (acc, k) => ({ ...acc, [k]: { ...DEFAULT_FEATURE, enabled: k !== "full_maintenance" } }),
          {}
        );
        setFeatures(def);
        setOriginalFeatures(def);
      }
    } catch {
      const def = FEATURE_KEYS.concat("full_maintenance").reduce(
        (acc, k) => ({ ...acc, [k]: { ...DEFAULT_FEATURE, enabled: k !== "full_maintenance" } }),
        {}
      );
      setFeatures(def);
      setOriginalFeatures(def);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Sync schedule action to opposite of selected feature's current state
  useEffect(() => {
    if (!scheduleFeature) return;
    const enabled = !!features[scheduleFeature]?.enabled;
    setScheduleAction(enabled ? "disable" : "enable");
  }, [scheduleFeature, features]);

  const fullMaintenance = features.full_maintenance ?? DEFAULT_FEATURE;

  const getChangesSummary = () => {
    const changes = [];
    const keys = [...FEATURE_KEYS, "full_maintenance"];
    const labels = { full_maintenance: "Full Maintenance", ...Object.fromEntries(FEATURE_ITEMS.map((i) => [i.key, i.label])) };
    keys.forEach((key) => {
      const orig = originalFeatures[key];
      const curr = features[key];
      if (orig == null && curr == null) return;
      const fromEnabled = !!orig?.enabled;
      const toEnabled = !!curr?.enabled;
      if (fromEnabled !== toEnabled) {
        changes.push({
          label: labels[key] || key.replace(/_/g, " "),
          from: fromEnabled ? "On" : "Off",
          to: toEnabled ? "On" : "Off",
        });
      }
    });
    return changes;
  };

  // eslint-disable-next-line no-unused-vars -- used when Save button onClick is enabled
  const handleSaveClick = () => {
    const changes = getChangesSummary();
    if (changes.length === 0) {
      showToast("No changes to save.", "info");
      return;
    }
    setConfirmModal(true);
  };

  const performSave = async () => {
    setConfirmModal(false);
    const payload = {};
    FEATURE_KEYS.forEach((key) => {
      const f = features[key];
      if (f != null) payload[key] = !!f.enabled;
    });
    if (fullMaintenance != null) payload.full_maintenance = !!fullMaintenance.enabled;

    setSaveLoading(true);
    try {
      const res = await AdminService.updatePlatformConfig(payload);
      if (res?.success && res.data) {
        const normalized = {};
        Object.keys(res.data).forEach((key) => {
          const v = res.data[key];
          normalized[key] = v && typeof v === "object"
            ? { enabled: !!v.enabled, scheduled_at: v.scheduled_at || null, scheduled_action: v.scheduled_action || null }
            : { ...DEFAULT_FEATURE, enabled: !!v };
        });
        setFeatures(normalized);
        setOriginalFeatures(normalized);
        showToast("Platform configuration saved successfully.");
        window.dispatchEvent(new CustomEvent("platformConfigUpdated"));
      } else {
        showToast(res?.message || "Failed to save.", "error");
      }
    } catch {
      showToast("Failed to save. Please try again.", "error");
    }
    setSaveLoading(false);
  };

  // eslint-disable-next-line no-unused-vars -- used when Schedule button onClick is enabled
  const handleScheduleChange = async () => {
    const key = scheduleFeature;
    if (!key || !scheduleDatetime) {
      showToast("Select a feature and a future date/time.", "error");
      return;
    }
    const dt = new Date(scheduleDatetime);
    if (dt <= new Date()) {
      showToast("scheduled_at must be in the future.", "error");
      return;
    }
    const payload = {
      [key]: {
        enabled: !!(features[key]?.enabled),
        scheduled_at: dt.toISOString(),
        scheduled_action: scheduleAction,
      },
    };
    setScheduleLoading(key);
    try {
      const res = await AdminService.updatePlatformConfig(payload);
      if (res?.success && res.data) {
        const v = res.data[key];
        setFeatures((prev) => ({
          ...prev,
          [key]: v && typeof v === "object"
            ? { enabled: !!v.enabled, scheduled_at: v.scheduled_at || null, scheduled_action: v.scheduled_action || null }
            : prev[key],
        }));
        showToast(`Scheduled ${scheduleAction} for ${key.replace(/_/g, " ")}.`);
        setScheduleDatetime("");
      } else {
        showToast(res?.message || "Failed to schedule.", "error");
      }
    } catch {
      showToast("Failed to schedule. Please try again.", "error");
    }
    setScheduleLoading(null);
  };

  const handleCancelSchedule = async (key) => {
    setCancelLoading(key);
    try {
      const res = await AdminService.cancelPlatformSchedule(key);
      if (res?.success && res.data) {
        const v = res.data[key];
        setFeatures((prev) => ({
          ...prev,
          [key]: v && typeof v === "object"
            ? { enabled: !!v.enabled, scheduled_at: null, scheduled_action: null }
            : prev[key],
        }));
        showToast(`Scheduled change for ${key.replace(/_/g, " ")} cancelled.`);
      } else {
        showToast(res?.message || "Failed to cancel schedule.", "error");
      }
    } catch {
      showToast("Failed to cancel schedule. Please try again.", "error");
    }
    setCancelLoading(null);
  };

  const setFullMaintenance = (enabled) => {
    setFeatures((prev) => ({
      ...prev,
      full_maintenance: { ...(prev.full_maintenance ?? DEFAULT_FEATURE), enabled },
    }));
  };

  const setFeatureEnabled = (key, enabled) => {
    setFeatures((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? DEFAULT_FEATURE), enabled },
    }));
  };

  if (loading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Platform Configuration</span>
            </p>
            <h1 className="dashboard-hero__title">Platform Configuration</h1>
          </div>
        </section>
        <div className="platform-loading">
          <i className="fas fa-spinner fa-spin" /> Loading…
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage platform-config-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Platform Configuration</span>
          </p>
          <h1 className="dashboard-hero__title">Platform Configuration</h1>
          <p className="dashboard-hero__subtitle">
            Control availability of platform features: instant on/off or schedule future changes
          </p>
        </div>
        <div className="dashboard-hero__icon platform-hero__icon">
          <i className="fas fa-cog" />
        </div>
      </section>

      <div className="dashboard-body platform-body">
        {/* Full System Maintenance */}
        <div className="platform-card platform-card--maintenance">
          <div className="platform-card__header">
            <span className="platform-card__icon platform-card__icon--danger">
              <i className="fas fa-exclamation-triangle" />
            </span>
            <div>
              <h2 className="platform-card__title">Full System Maintenance</h2>
              <p className="platform-card__desc">When enabled, the entire platform will be inaccessible to users.</p>
              {fullMaintenance.scheduled_at != null && fullMaintenance.scheduled_at !== "" && (
                <div className="platform-schedule-badge">
                  <i className="fas fa-clock" /> Scheduled {fullMaintenance.scheduled_action} at{" "}
                  {new Date(fullMaintenance.scheduled_at).toLocaleString()}
                  <button
                    type="button"
                    className="platform-cancel-chip"
                    onClick={() => handleCancelSchedule("full_maintenance")}
                    disabled={cancelLoading === "full_maintenance"}
                  >
                    {cancelLoading === "full_maintenance" ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      "Cancel"
                    )}
                  </button>
                </div>
              )}
            </div>
            <label className="platform-toggle">
              <input
                type="checkbox"
                checked={!!fullMaintenance.enabled}
                onChange={(e) => setFullMaintenance(e.target.checked)}
              />
              <span className="platform-toggle__slider" />
            </label>
          </div>
        </div>

        {/* Service Toggles (Instant) */}
        <div className="platform-card">
          <div className="platform-card__header">
            <span className="platform-card__icon">
              <i className="fas fa-plug" />
            </span>
            <div>
              <h2 className="platform-card__title">Service Controls (Instant On/Off)</h2>
              <p className="platform-card__desc">Enable or disable individual services. Click Save to apply changes.</p>
            </div>
          </div>
          <div className="platform-card__body">
            <div className="platform-services-grid">
              {FEATURE_ITEMS.map((item) => {
                const f = features[item.key] ?? DEFAULT_FEATURE;
                const hasSchedule = f.scheduled_at != null && f.scheduled_at !== "";
                return (
                  <div key={item.key} className="platform-service-item">
                    <div className="platform-service-item__info">
                      <i className={`fas ${item.icon} platform-service-item__icon`} />
                      <div>
                        <span className="platform-service-item__label">{item.label}</span>
                        <span className="platform-service-item__desc">{item.desc}</span>
                        {hasSchedule && (
                          <div className="platform-schedule-badge">
                            <i className="fas fa-clock" /> Scheduled {f.scheduled_action} at{" "}
                            {new Date(f.scheduled_at).toLocaleString()}
                            <button
                              type="button"
                              className="platform-cancel-chip"
                              onClick={() => handleCancelSchedule(item.key)}
                              disabled={cancelLoading === item.key}
                            >
                              {cancelLoading === item.key ? (
                                <i className="fas fa-spinner fa-spin" />
                              ) : (
                                "Cancel"
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <label className="platform-toggle platform-toggle--sm">
                      <input
                        type="checkbox"
                        checked={!!f.enabled}
                        onChange={() => setFeatureEnabled(item.key, !f.enabled)}
                        disabled={fullMaintenance.enabled}
                      />
                      <span className="platform-toggle__slider" />
                    </label>
                  </div>
                );
              })}
            </div>
            {fullMaintenance.enabled && (
              <p className="platform-hint">
                <i className="fas fa-info-circle" /> Service toggles are disabled when maintenance mode is on.
              </p>
            )}
          </div>
        </div>

          {/* Save Button */}
          <div className="platform-actions">
          <button
            type="button"
            className="cp-btn cp-btn--primary"
            // onClick={handleSaveClick}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <><i className="fas fa-spinner fa-spin" /> Saving…</>
            ) : (
              <><i className="fas fa-save" /> Save Configuration</>
            )}
          </button>
        </div>

        {/* Scheduled Changes */}
        <div className="platform-card">
          <div className="platform-card__header">
            <span className="platform-card__icon platform-card__icon--schedule">
              <i className="fas fa-calendar-alt" />
            </span>
            <div>
              <h2 className="platform-card__title">Schedule a Change</h2>
              <p className="platform-card__desc">
                Set a future time to enable or disable a feature. A cron job applies changes automatically.
              </p>
            </div>
          </div>
          <div className="platform-card__body platform-scheduled-form">
            <div className="platform-scheduled-grid">
              <div className="cp-field">
                <label>Feature</label>
                <div className="cp-input-wrap">
                  <select
                    value={scheduleFeature}
                    onChange={(e) => setScheduleFeature(e.target.value)}
                    className="platform-select"
                  >
                    <option value="">Select feature…</option>
                    {FEATURE_ITEMS.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.label}
                      </option>
                    ))}
                    <option value="full_maintenance">Full Maintenance</option>
                  </select>
                </div>
              </div>
              <div className="cp-field">
                <label>Action (opposite of current state)</label>
                <div className="cp-input-wrap">
                  <select
                    value={scheduleAction}
                    onChange={(e) => setScheduleAction(e.target.value)}
                    className="platform-select"
                    disabled={!scheduleFeature}
                  >
                    {scheduleFeature ? (
                      <>
                        {!!features[scheduleFeature]?.enabled && (
                          <option value="disable">Disable</option>
                        )}
                        {!features[scheduleFeature]?.enabled && (
                          <option value="enable">Enable</option>
                        )}
                      </>
                    ) : (
                      <option value="">Select feature first</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="cp-field">
                <label>Date & Time (must be in the future)</label>
                <div className="cp-input-wrap">
                  <i className="fas fa-calendar-plus" />
                  <input
                    type="datetime-local"
                    value={scheduleDatetime}
                    onChange={(e) => setScheduleDatetime(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              className="cp-btn cp-btn--outline"
              // onClick={handleScheduleChange}
              disabled={!scheduleFeature || !scheduleDatetime || scheduleLoading}
            >
              {scheduleLoading ? (
                <><i className="fas fa-spinner fa-spin" /> Scheduling…</>
              ) : (
                <><i className="fas fa-calendar-check" /> Schedule</>
              )}
            </button>
          </div>
        </div>

      
      </div>

      {confirmModal && (() => {
        const changes = getChangesSummary();
        return (
          <div className="platform-confirm-overlay" onClick={() => setConfirmModal(false)}>
            <div className="platform-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="platform-confirm-title">
                <i className="fas fa-exclamation-circle" /> Confirm Changes
              </h3>
              <p className="platform-confirm-text">
                You are about to apply the following changes. Review before continuing:
              </p>
              <div className="platform-changes-list">
                {changes.map((c, i) => (
                  <div key={i} className="platform-change-row">
                    <span className="platform-change-label">{c.label}</span>
                    <span className="platform-change-from">{c.from}</span>
                    <span className="platform-change-arrow"><i className="fas fa-arrow-right" /></span>
                    <span className="platform-change-to">{c.to}</span>
                  </div>
                ))}
              </div>
              <div className="platform-confirm-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={() => setConfirmModal(false)}>Cancel</button>
                <button type="button" className="cp-btn cp-btn--primary" onClick={performSave} disabled={saveLoading}>
                  {saveLoading ? <><i className="fas fa-spinner fa-spin" /> Saving…</> : <><i className="fas fa-check" /> Apply</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {toast && (
        <div className={`platform-toast platform-toast--${toast.type}`}>
          <i className={`fas ${toast.type === "success" ? "fa-check-circle" : toast.type === "error" ? "fa-exclamation-circle" : "fa-info-circle"}`} />
          <span>{toast.msg}</span>
        </div>
      )}
    </main>
  );
};

export default PlatformConfigurationPage;
